import React, { type CSSProperties, type ReactNode } from 'react';

interface SkeletonShapeProps {
  width?: number | string;
  height?: number;
  radius?: number;
  shimmer?: boolean;
  style?: CSSProperties;
}

const shimmerStyle: CSSProperties = {
  background:
    'linear-gradient(90deg, var(--tabbio-surface-secondary) 25%, var(--tabbio-surface-tertiary) 50%, var(--tabbio-surface-secondary) 75%)',
  backgroundSize: '200% 100%',
  animation: 'tabbioSkeletonShimmer 1.5s ease-in-out infinite',
};

const staticStyle: CSSProperties = {
  backgroundColor: 'var(--tabbio-surface-secondary)',
};

function ShimmerKeyframes() {
  return (
    <style>{`
      @keyframes tabbioSkeletonShimmer {
        0% { background-position: -200% 0; }
        100% { background-position: 200% 0; }
      }
    `}</style>
  );
}

export function Skeleton({
  width = '100%',
  height = 14,
  radius = 8,
  shimmer = true,
  style,
}: SkeletonShapeProps) {
  return (
    <>
      {shimmer ? <ShimmerKeyframes /> : null}
      <div
        aria-hidden
        style={{
          ...(shimmer ? shimmerStyle : staticStyle),
          width,
          height,
          borderRadius: radius,
          flexShrink: 0,
          ...style,
        }}
      />
    </>
  );
}

export function SkeletonLine({
  width = '75%',
  height = 12,
  shimmer,
}: {
  width?: number | string;
  height?: number;
  shimmer?: boolean;
}) {
  return <Skeleton width={width} height={height} radius={6} shimmer={shimmer} />;
}

export function SkeletonCircle({
  size = 40,
  shimmer,
}: {
  size?: number;
  shimmer?: boolean;
}) {
  return <Skeleton width={size} height={size} radius={size / 2} shimmer={shimmer} />;
}

export function SkeletonPill({
  width = 56,
  height = 20,
  shimmer,
}: {
  width?: number | string;
  height?: number;
  shimmer?: boolean;
}) {
  return <Skeleton width={width} height={height} radius={999} shimmer={shimmer} />;
}

export function SkeletonBlock({
  width = '100%',
  height = 80,
  radius = 12,
  shimmer,
}: SkeletonShapeProps) {
  return <Skeleton width={width} height={height} radius={radius} shimmer={shimmer} />;
}

export function SkeletonStack({
  children,
  gap = 8,
}: {
  children: ReactNode;
  gap?: number;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap, flex: 1 }}>
      {children}
    </div>
  );
}
